import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { makeRequest } from "../util/AxiosInstance";
import "photoswipe/dist/photoswipe.css";
import fileIcon from "../assets/file.svg";
import { Gallery, Item } from "react-photoswipe-gallery";
import { formatFileName } from "../util/functions";
import { useFileManager } from "../hooks/useFileManager";
import { filesize } from "filesize";
import { IoCloudDownloadOutline } from "react-icons/io5";
import { isArray } from "underscore";
import { LoadingButton } from "@mui/lab";


const FileMessage = ({ message }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const fileRef = useRef(null);

  const { uploadFile, isUploading, error: uploadError, uploadProgress } = useFileManager()

  useEffect(() => {
    if (uploadError) {
      console.log('uploadError: ', uploadError);
    }
  }, [uploadError]);

  useEffect(() => {
    const getLink = async (fileId) => {
      const { data } = await makeRequest("POST", `/chat/links`, [fileId]);
      const link = data?.data[0];
      return link;
    };

    const meta = message.meta;
    if (!meta || !isArray(meta)) {
      return;
    }
    meta.forEach((value) => {
      if (value.status !== 'completed') {
        uploadFile(value, message);
      }
    })
  }, [message]);

  const handleDownload = async (file) => {
    const { link: downloadUrl, mimeType, name } = file;
    if (downloadUrl) {
      setIsDownloading(true);
      setDownloadError(null);
      try {
        const response = await axios.get(downloadUrl, {
          responseType: "blob",
          onDownloadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setDownloadProgress(percentCompleted);
          },
        });

        const contentType = response.headers["content-type"];
        const contentDisposition = response.headers["content-disposition"];
        let fileName = name || "download";

        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/i);
          if (fileNameMatch && fileNameMatch[1]) {
            fileName = fileNameMatch[1];
          }
        }
        const blob = new Blob([response.data], { type: contentType });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Download error:", error);
        setDownloadError(
          "An error occurred during download. Please try again."
        );
      } finally {
        setIsDownloading(false);
        setDownloadProgress(0);
      }
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "10px",
        position: 'relative',
      }}
    >
      <>
        {isUploading && <>
          <div className="absolute w-full h-full top-0 left-0 z-10 bg-gray-500 opacity-80 z-10 flex justify-center items-center bg-gray-900">
            <div
              className="radial-progress text-white"
              style={{
                "--value": uploadProgress[0],
                "--size": "3rem",
                "--thickness": "2px",
              }}
              role="progressbar"
            >
              <span className="text-sm">
                {uploadProgress[0]}%
              </span>
            </div>
          </div>
        </>}
        <div ref={fileRef} className="file-message-item ">
          <p className="text-primary-content">{message.content}</p>
          <>
            <Gallery>
              {'meta' in message &&
                message.meta &&
                isArray(message.meta) &&
                message.meta?.map((file, index) => {
                  return <Item
                    original={file?.link}
                    thumbnail={file?.link}
                    width="1024"
                    height="768"
                    key={index}
                  >
                    {({ ref, open }) => {
                      return (
                        <div className="relative file-message-item">
                          {file?.type === 'image' && (
                            <img ref={ref} onClick={open} alt="" src={file?.link} />
                          )}
                          {file?.type === 'video' && (
                            <video ref={ref} controls src={file?.link} />
                          )}
                          {file?.type === 'document' && (
                            <div className="flex items-center justify-center card w-full ">
                              <div className="card-body flex flex-row  items-center gap-1 shadow-sm rounded-lg bg-slate-50 p-2 min-w-[250px] min-h-[100px]">
                                <div>
                                  <img
                                    src={fileIcon}
                                    alt=""
                                    className="h-12 w-12"
                                  />
                                </div>
                                <div className="flex flex-row gap-1 justify-between items-start flex-1">
                                  <div className="flex flex-col flex-1">
                                    <p className="text-wrap text-primary-content">
                                      {formatFileName(file?.name)}
                                    </p>
                                    <span className="text-xs text-gray-500">
                                      {filesize(file?.size)}
                                    </span>
                                  </div>
                                  <div className="ml-auto flex place-items-center">
                                    <LoadingButton
                                      className="btn btn-circle btn-primary btn-outline"
                                      color="secondary"
                                      style={{
                                        borderRadius: "50%",
                                        border: "none",
                                        padding: "1px",
                                        fontWeight: "bold",
                                        width: "20px",
                                        height: "20px",
                                      }}
                                      loading={isDownloading}
                                      onClick={() => handleDownload(file)}
                                      disabled={!file.link}
                                    >
                                      <IoCloudDownloadOutline />
                                    </LoadingButton>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                          {uploadProgress[index] !== undefined &&
                            uploadProgress[index] >= 0 &&
                            uploadProgress[index] < 100 && (
                              <div className="absolute top-0 left-0 w-full flex justify-center items-center h-full bg-gray-500 opacity-80">
                                <div
                                  className="radial-progress text-white"
                                  style={{
                                    "--value": uploadProgress[index],
                                    "--size": "3rem",
                                    "--thickness": "2px",
                                  }}
                                  role="progressbar"
                                >
                                  <span className="text-sm">
                                    {uploadProgress[index]}%
                                  </span>
                                </div>
                              </div>
                            )}
                        </div>
                      );
                    }}
                  </Item>
                })}
            </Gallery>
          </>
        </div>
      </>
    </div>
  );
};

export default FileMessage;
